import { BroadcastChannelBrowserBroadcast } from '@adapters/broadcast/broadcastChannelBrowserBroadcast';
import { InmemoryBrowserBroadcast } from '@adapters/broadcast/inmemoryBrowserBroadcast';
import { TagCommanderMetricMapper } from '@adapters/tagging/tagCommanderMetricMapper';
import { TaggingMetricMapper } from '@adapters/tagging/taggingMetricMapper';
import { AngularClient } from '@core/gateway/http/angularClient';
import { AppInsightsLogger } from '@core/gateway/logger/appInsightsLogger';
import { InmemoryLogger } from '@core/gateway/logger/inmemoryLogger';
import { WaitOnCaseStateNotificationSwitcher } from '@core/gateway/notification/caseState/waitOnCaseStateNotificationSwitcher';
import { WaitOnCaseStateNotificationViaPolling } from '@core/gateway/notification/caseState/waitOnCaseStateNotificationViaPolling';
import { WaitOnCaseStateNotificationViaSignalR } from '@core/gateway/notification/caseState/waitOnCaseStateNotificationViaSignalR';
import { WaitOnWebauthNotificationSwitcher } from '@core/gateway/notification/webauth/waitOnWebauthNotificationSwitcher';
import { WaitOnWebauthNotificationViaPolling } from '@core/gateway/notification/webauth/waitOnWebauthNotificationViaPolling';
import { WaitOnWebauthNotificationViaSignalR } from '@core/gateway/notification/webauth/waitOnWebauthNotificationViaSignalR';
import { TagCommanderTracker } from '@core/gateway/tagging/tagCommanderTracker';
import { AccountMapper } from '@core/mapper/accountMapper';
import { BankConnectionLinkedNotificationMapper } from '@core/mapper/bankConnectionLinkedNotificationMapper';
import { BankConnectionMapper } from '@core/mapper/bankConnectionMapper';
import { BankConnectionStateMapper } from '@core/mapper/bankConnectionStateMapper';
import { BankMapper } from '@core/mapper/bankMapper';
import { CaseStateMapper } from '@core/mapper/caseStateMapper';
import { TaggingTracker } from '@core/model/domain/taggingTracker';
import { NotificationSwitcher } from '@core/model/framework/notifications/notificationSwitcher';
import { SignalR } from '@core/model/framework/notifications/signalR';
import { DFAccountService } from '@core/service/digital-factory/account/DFAccountService';
import { DFBankService } from '@core/service/digital-factory/bank/DFBankService';
import { IMAccountService } from '@core/service/in-memory/IMAccountService';
import { IMBankService } from '@core/service/in-memory/IMBankService';
import { GetBankConnections } from '@core/usecase/getBankConnections';
import { GetBanks } from '@core/usecase/getBanks';
import { GetCaseState } from '@core/usecase/getCaseState';
import { GetWebauthUrl } from '@core/usecase/getWebauthUrl';
import { SaveWebauthBankConnection } from '@core/usecase/saveWebauthBankConnection';
import { WaitOnCaseStateNotification } from '@core/usecase/waitOnCaseStateNotification';
import { WaitOnWebauthNotification } from '@core/usecase/waitOnWebauthNotification';
import { Container } from 'inversify';
import type { TagCommanderService } from 'ngx-tag-commander';
import type { Newable } from 'ts-essentials';
import type { KernelConfiguration } from './kernelConfiguration';
import { SERVICE_ID } from './serviceId';

export class Kernel {
  private container: Container;

  private constructor(private readonly configuration: KernelConfiguration) {
    this.container = new Container();
  }

  static initialize(configuration: KernelConfiguration): Kernel {
    const kernel = new Kernel(configuration);

    kernel.setupHttpClients();
    kernel.setupServices();
    kernel.setupGateways();
    kernel.setupUsecases();
    kernel.setupMappers();
    kernel.setupConstants();
    kernel.setupFeatureFlags();
    return kernel;
  }

  public bindTaggingTracker(tagCommanderService: TagCommanderService): TaggingTracker {
    const taggingContainers = this.container.get(SERVICE_ID.taggingContainers);
    const tagCommanderTracker = new TagCommanderTracker(tagCommanderService, taggingContainers);
    this.container.bind(SERVICE_ID.taggingTracker).toConstantValue(tagCommanderTracker);

    return this.container.get(SERVICE_ID.taggingTracker);
  }

  public bindTaggingMetricMapper(tagCommanderMetricMapper: TagCommanderMetricMapper): TaggingMetricMapper {
    this.container.bind(SERVICE_ID.taggingMetricMapper).toConstantValue(tagCommanderMetricMapper);

    return this.container.get(SERVICE_ID.taggingMetricMapper);
  }

  private setupFeatureFlags() {
    let shouldInitializeSignalR = false;
    for (const key in this.configuration.notifications) {
      if (this.configuration.notifications[key].method === 'signalR') {
        shouldInitializeSignalR = true;
      }
    }

    this.container.bind(SERVICE_ID.signalRFeatureFlag).toConstantValue(shouldInitializeSignalR);
  }

  private setupHttpClients() {
    const openBankingAngularHttpClient = new AngularClient(this.configuration.openBankingBaseUrl);
    this.container.bind(SERVICE_ID.openBankingHttpClient).toConstantValue(openBankingAngularHttpClient);
  }

  private setupServices() {
    if (this.configuration.isInMemory) {
      this.container.bind(SERVICE_ID.bankService).to(IMBankService);
      this.container.bind(SERVICE_ID.accountService).to(IMAccountService);
      this.container.bind(SERVICE_ID.browserBroadcast).to(InmemoryBrowserBroadcast);
      this.container.bind(SERVICE_ID.logger).to(InmemoryLogger);
    } else {
      this.container.bind(SERVICE_ID.bankService).to(DFBankService);
      this.container.bind(SERVICE_ID.accountService).to(DFAccountService);
      this.container.bind(SERVICE_ID.browserBroadcast).to(BroadcastChannelBrowserBroadcast);
      this.container.bind(SERVICE_ID.logger).to(AppInsightsLogger);
    }

    this.container.bind(SERVICE_ID.signalR).toConstantValue(new SignalR());
    this.container.bind(SERVICE_ID.notificationSwitcher).to(NotificationSwitcher).inSingletonScope();
  }

  private setupUsecases() {
    this.container.bind(SaveWebauthBankConnection).toSelf();
    this.container.bind(GetBankConnections).toSelf();
    this.container.bind(WaitOnWebauthNotification).toSelf();
    this.container.bind(GetBanks).toSelf();
    this.container.bind(GetWebauthUrl).toSelf();
    this.container.bind(GetCaseState).toSelf();
    this.container.bind(WaitOnCaseStateNotification).toSelf();

    this.container
      .bind(SERVICE_ID.waitOnWebauthNotificationViaSignalR)
      .to(WaitOnWebauthNotificationViaSignalR);
    this.container
      .bind(SERVICE_ID.waitOnWebauthNotificationViaPolling)
      .to(WaitOnWebauthNotificationViaPolling);
    this.container
      .bind(SERVICE_ID.waitOnCaseStateNotificationViaPolling)
      .to(WaitOnCaseStateNotificationViaPolling);
    this.container
      .bind(SERVICE_ID.waitOnCaseStateNotificationViaSignalR)
      .to(WaitOnCaseStateNotificationViaSignalR);
  }

  private setupMappers() {
    this.container.bind(AccountMapper).toSelf();
    this.container.bind(BankConnectionStateMapper).toSelf();
    this.container.bind(BankMapper).toSelf();
    this.container.bind(BankConnectionMapper).toSelf();
    this.container.bind(BankConnectionLinkedNotificationMapper).toSelf();
    this.container.bind(CaseStateMapper).toSelf();
  }

  private setupGateways() {
    this.container.bind(SERVICE_ID.waitOnWebauthNotificationGateway).to(WaitOnWebauthNotificationSwitcher);

    this.container
      .bind(SERVICE_ID.waitOnCaseStateNotificationGateway)
      .to(WaitOnCaseStateNotificationSwitcher);
  }

  private setupConstants() {
    this.container
      .bind(SERVICE_ID.waitOnWebauthTimeoutInSeconds)
      .toConstantValue(this.configuration.notifications.webauth.timeoutInSeconds);

    this.container
      .bind(SERVICE_ID.waitOnWebauthIntervalInSeconds)
      .toConstantValue(this.configuration.notifications.webauth.intervalInSeconds);

    this.container
      .bind(SERVICE_ID.waitOnCaseStateTimeoutInSeconds)
      .toConstantValue(this.configuration.notifications.caseState.timeoutInSeconds);

    this.container
      .bind(SERVICE_ID.waitOnCaseStateIntervalInSeconds)
      .toConstantValue(this.configuration.notifications.caseState.intervalInSeconds);

    this.container.bind(SERVICE_ID.isTrackingEnabled).toConstantValue(this.configuration.isTrackingEnabled);
    this.container.bind(SERVICE_ID.taggingContainers).toConstantValue(this.configuration.taggingContainers);
  }

  getByIdentifier<T>(identifier: symbol | Newable<T>): T {
    if (!this.container.isBound(identifier)) {
      throw new Error(`Identifier is not bound ${identifier.toString()}`);
    }
    return this.container.get<T>(identifier);
  }

  isBound<T>(identifier: symbol | Newable<T>): boolean {
    return this.container.isBound(identifier);
  }
}
